import getBrowser from "@core/utils/helpers/getBrowser";
import { Loader } from "@googlemaps/js-api-loader";
import React, { FC, useEffect, useMemo, useRef } from "react";

type LocationItem = {
  FormattedAddress?: string;
  Lat: string;
  Lng: string;
  PlaceID?: string;
};

type Props = {
  data: string | LocationItem[];
};

const DEFAULT_CENTER = { lat: 50.4501, lng: 30.5234 }; // Kyiv
const DEFAULT_ZOOM = 8;

const GMAPS_API_KEY = "AIzaSyDshC1CWnnSf92AlC-5w0-o6DLAfQQZs70";
const GMAPS_MAP_ID = "a2bb0b0a8f621b59"; 

export const Map: FC<Props> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const boundsRef = useRef<google.maps.LatLngBounds | null>(null);

  const loader = useMemo(
    () =>
      new Loader({
        apiKey: GMAPS_API_KEY,
        version: "quarterly",
        libraries: ["places"],
      }),
    []
  );

  const isBoundsEmpty = (b: google.maps.LatLngBounds | null) => {
    if (!b) return true;
    // @ts-ignore старі версії можуть не мати isEmpty
    return typeof b.isEmpty === "function" ? b.isEmpty() : false;
  };

  const scheduleRefit = (
    map: google.maps.Map,
    refit: () => void,
    delays: number[] = [0, 150, 350]
  ) => {
    delays.forEach((d) => {
      setTimeout(() => {
        // @ts-ignore
        google.maps.event.trigger(map, "resize");
        refit();
      }, d);
    });
  };

  const addNewMarker = async (
    obj: { Lat: string; Lng: string },
    map: google.maps.Map | undefined,
    bounds: google.maps.LatLngBounds
  ): Promise<void> => {
    if (!map) return;

    const lat = +obj.Lat;
    const lng = +obj.Lng;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    try {
      const markerLib: any = await loader.importLibrary("marker");
      const canUseAdvanced =
        !!markerLib?.AdvancedMarkerElement && !!(map as any)?.get?.("mapId");

      if (canUseAdvanced) {
        new markerLib.AdvancedMarkerElement({
          map,
          position: { lat, lng },
        });
      } else {
        new google.maps.Marker({
          map,
          position: { lat, lng },
        });
      }
    } catch {
      new google.maps.Marker({
        map,
        position: { lat, lng },
      });
    }

    bounds.extend({ lat, lng });
  };

  const handleSingleLocation = async (
    address: string | LocationItem,
    map: google.maps.Map,
    geocoder: google.maps.Geocoder,
    bounds: google.maps.LatLngBounds
  ) => {
    if (typeof address === "string") {
      try {
        const result = await geocoder.geocode({ address });
        const { results } = result as any;
        if (results?.length > 0) {
          const latlng = {
            Lat: results[0].geometry.location.lat().toString(),
            Lng: results[0].geometry.location.lng().toString(),
          };
          await addNewMarker(latlng, map, bounds);
          const lat = +latlng.Lat;
          const lng = +latlng.Lng;
          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            const refit = () => {
              map.setCenter({ lat, lng });
              map.setZoom(14);
            };
            refit();
            scheduleRefit(map, refit);
          }
        } else {
          map.setCenter(DEFAULT_CENTER);
          map.setZoom(DEFAULT_ZOOM);
        }
      } catch (e) {
        console.error("Geocode error:", e);
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(DEFAULT_ZOOM);
      }
    } else {
      const lat = +address.Lat;
      const lng = +address.Lng;
      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(DEFAULT_ZOOM);
        return;
      }
      await addNewMarker(address, map, bounds);
      const refit = () => {
        map.setCenter({ lat, lng });
        map.setZoom(14);
      };
      refit();
      scheduleRefit(map, refit);
    }
  };

  const handleMultipleLocations = async (
    locations: LocationItem[],
    map: google.maps.Map,
    geocoder: google.maps.Geocoder,
    bounds: google.maps.LatLngBounds
  ) => {
    const uniqueCoordinates = new Set<string>();

    const tasks = locations.map(async (obj) => {
      if (!obj?.Lat || !obj?.Lng) return;

      const latLngString = `${obj.Lat},${obj.Lng}`;
      if (uniqueCoordinates.has(latLngString)) return;
      uniqueCoordinates.add(latLngString);

      if (obj.FormattedAddress) {
        try {
          const result: any = await geocoder.geocode({
            address: obj.FormattedAddress,
          });
          const { results } = result;
          if (results?.length > 0) {
            const latlng = {
              Lat: results[0].geometry.location.lat().toString(),
              Lng: results[0].geometry.location.lng().toString(),
            };
            await addNewMarker(latlng, map, bounds);
            return;
          }
        } catch (e) {
          console.error("Geocode error:", e);
        }
      }
      await addNewMarker({ Lat: obj.Lat, Lng: obj.Lng }, map, bounds);
    });

    await Promise.all(tasks);

    const refit = () => {
      if (uniqueCoordinates.size === 1) {
        const [Lat, Lng] = Array.from(uniqueCoordinates)[0].split(",");
        const lat = +Lat,
          lng = +Lng;
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          map.setCenter({ lat, lng });
          map.setZoom(14);
          return;
        }
      }
      if (!isBoundsEmpty(bounds)) {
        map.fitBounds(bounds);
      } else {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(DEFAULT_ZOOM);
      }
    };

    refit();
    scheduleRefit(map, refit);
  };

  useEffect(() => {
    let ro: ResizeObserver | null = null;
    let cancelled = false;

    const initMap = async () => {
      try {
        const [maps, geocoding, core] = await Promise.all([
          loader.importLibrary("maps"),
          loader.importLibrary("geocoding"),
          loader.importLibrary("core"),
        ]);

        if (!containerRef.current) return;

        const map = new (maps as any).Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          disableDefaultUI: true,
          ...(GMAPS_MAP_ID ? { mapId: GMAPS_MAP_ID } : {}),
        });

        const geocoder = new (geocoding as any).Geocoder();
        const bounds = new (core as any).LatLngBounds();

        mapRef.current = map;
        boundsRef.current = bounds;

        if (typeof data === "string" || (Array.isArray(data) && data.length === 1)) {
          const item = typeof data === "string" ? data : data[0];
          await handleSingleLocation(item as any, map, geocoder, bounds);
        } else if (Array.isArray(data) && data.length > 0) {
          await handleMultipleLocations(data as LocationItem[], map, geocoder, bounds);
        } else {
          map.setCenter(DEFAULT_CENTER);
          map.setZoom(DEFAULT_ZOOM);
        }

        ro = new ResizeObserver(() => {
          if (!mapRef.current) return;
          // @ts-ignore
          google.maps.event.trigger(mapRef.current, "resize");
          if (boundsRef.current && !isBoundsEmpty(boundsRef.current)) {
            mapRef.current.fitBounds(boundsRef.current);
          }
        });
        ro.observe(containerRef.current);
      } catch (e) {
        if (!cancelled) {
          console.error("Map initialization failed:", e);
        }
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (ro) ro.disconnect();
      mapRef.current = null;
      boundsRef.current = null;
    };
  }, [JSON.stringify(data)]);

  useEffect(() => {
    console.log(getBrowser());
  }, []);

  return <div ref={containerRef} style={{ height: "85%", width: "100%" }} />;
};
